import { ReactNode, Ref } from 'react';
import { cn } from 'utils/cn';

/** A section header's link, and the TiqTime link that can stand in its place. */
export const HOME_LINK_CLASS = 'text-primary hover:text-link text-meta font-medium no-underline hover:underline';

/** A section's tab strip, with `LineTab size="compact"`: 34px tabs in 13px type on a light rule. */
export const HOME_TAB_LIST_CLASS = 'border-neutral-tint new:gap-0.5';
export const HOME_TAB_CLASS = 'text-meta min-h-[34px] px-2.5 py-0';

type Props = {
    title: ReactNode;
    /** Controls and links at the end of the heading row. */
    actions?: ReactNode;
    children: ReactNode;
    /** The card's padding and the gap under its heading row, where a section differs from the default. */
    className?: string;
    ref?: Ref<HTMLElement>;
    /** Where the guided tour points. */
    'data-tour'?: string;
};

/**
 * One Home section: a card with its heading row inside, then the section's content. It fills the grid cell it is
 * given, so the part of the content that can outgrow the cell scrolls: that part takes `min-h-0 flex-1` and its own
 * overflow.
 */
export default function HomeSection({ title, actions, children, className, ref, 'data-tour': tourTarget }: Props) {
    return (
        <section
            ref={ref}
            data-tour={tourTarget}
            className={cn(
                'bg-card border-border new:border-0 new:shadow-card new:outline-hidden flex h-full min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden rounded-[10px] border px-5 pt-4 pb-2',
                className
            )}
        >
            <div className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1">
                <h3 className="text-section text-foreground flex-1 whitespace-nowrap">{title}</h3>
                {actions}
            </div>
            {children}
        </section>
    );
}
